import { EmailRequest } from './documentService';

export interface EmailTemplate {
  subject: string;
  body: string;
  htmlBody?: string;
}

export class EmailService {
  // Generate email template for document summary
  static generateDocumentEmailTemplate(
    clientName: string,
    documentTitle: string,
    summary: string,
    language: string = 'English',
    pdfUrl?: string
  ): EmailTemplate {
    const isHebrew = language === 'Hebrew';
    
    const subject = isHebrew 
      ? `עדכון מחקר: ${documentTitle}`
      : `Research Update: ${documentTitle}`;
    
    const body = isHebrew
      ? `שלום ${clientName},

הנכם מקבלים סיכום מקצועי של המסמך הבא:

${documentTitle}

סיכום מקצועי:
${summary}

${pdfUrl ? `קובץ PDF מצורף: ${pdfUrl}` : ''}

בברכה,
צוות פסגות

---
זהו דיוור אוטומטי. אנא אל תשיבו להודעה זו.`
      : `Dear ${clientName},

You are receiving a professional summary of the following document:

${documentTitle}

Professional Summary:
${summary}

${pdfUrl ? `PDF Document Attached: ${pdfUrl}` : ''}

Best regards,
Psagot Team

---
This is an automated email. Please do not reply to this message.`;

    const htmlBody = isHebrew
      ? `
        <!DOCTYPE html>
        <html dir="rtl">
        <head>
            <meta charset="utf-8">
            <meta name="viewport" content="width=device-width, initial-scale=1.0">
            <title>${subject}</title>
            <style>
                body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; }
                .container { max-width: 600px; margin: 0 auto; padding: 20px; }
                .header { background-color: #f8f9fa; padding: 20px; border-radius: 8px; }
                .content { padding: 20px; line-height: 1.6; }
                .footer { background-color: #f8f9fa; padding: 15px; border-radius: 8px; margin-top: 20px; }
                .pdf-link { display: inline-block; background-color: #007bff; color: white; padding: 10px 20px; text-decoration: none; border-radius: 5px; margin: 10px 0; }
            </style>
        </head>
        <body>
            <div class="container">
                <div class="header">
                    <h2>${subject}</h2>
                </div>
                <div class="content">
                    <p>שלום ${clientName},</p>
                    <p>הנכם מקבלים סיכום מקצועי של המסמך הבא:</p>
                    <h3>${documentTitle}</h3>
                    <h4>סיכום מקצועי:</h4>
                    <div style="background-color: #f5f5f5; padding: 15px; border-radius: 5px; margin: 10px 0;">
                        ${summary.replace(/\n/g, '<br>')}
                    </div>
                    ${pdfUrl ? `<p><strong>קובץ PDF מצורף:</strong> <a href="${pdfUrl}" target="_blank" class="pdf-link">לחץ כאן להורדה</a></p>` : ''}
                    <p>בברכה,<br>צוות פסגות</p>
                </div>
                <div class="footer">
                    <small>זהו דיוור אוטומטי. אנא אל תשיבו להודעה זו.</small>
                </div>
            </div>
        </body>
        </html>
      `
      : `
        <!DOCTYPE html>
        <html>
        <head>
            <meta charset="utf-8">
            <meta name="viewport" content="width=device-width, initial-scale=1.0">
            <title>${subject}</title>
            <style>
                body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; }
                .container { max-width: 600px; margin: 0 auto; padding: 20px; }
                .header { background-color: #f8f9fa; padding: 20px; border-radius: 8px; }
                .content { padding: 20px; line-height: 1.6; }
                .footer { background-color: #f8f9fa; padding: 15px; border-radius: 8px; margin-top: 20px; }
                .pdf-link { display: inline-block; background-color: #007bff; color: white; padding: 10px 20px; text-decoration: none; border-radius: 5px; margin: 10px 0; }
            </style>
        </head>
        <body>
            <div class="container">
                <div class="header">
                    <h2>${subject}</h2>
                </div>
                <div class="content">
                    <p>Dear ${clientName},</p>
                    <p>You are receiving a professional summary of the following document:</p>
                    <h3>${documentTitle}</h3>
                    <h4>Professional Summary:</h4>
                    <div style="background-color: #f5f5f5; padding: 15px; border-radius: 5px; margin: 10px 0;">
                        ${summary.replace(/\n/g, '<br>')}
                    </div>
                    ${pdfUrl ? `<p><strong>PDF Document Attached:</strong> <a href="${pdfUrl}" target="_blank" class="pdf-link">Click here to download</a></p>` : ''}
                    <p>Best regards,<br>Psagot Team</p>
                </div>
                <div class="footer">
                    <small>This is an automated email. Please do not reply to this message.</small>
                </div>
            </div>
        </body>
        </html>
      `;

    return {
      subject,
      body,
      htmlBody
    };
  }

  // Send email via backend API (for iPhone integration)
  static async sendEmail(
    to: string,
    template: EmailTemplate,
    metadata?: {
      clientId?: number;
      documentId?: number;
      sentBy?: string;
    }
  ): Promise<void> {
    try {
      console.log('📧 Sending email via backend API...');
      
      // Send email through backend API
      const response = await fetch('/api/send-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          to,
          subject: template.subject,
          text: template.body,
          html: template.htmlBody,
          metadata
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `Failed to send email (Status: ${response.status})`);
      }

      const result = await response.json();
      console.log('✅ Email sent successfully:', result);
      
    } catch (error) {
      console.error('❌ Error sending email:', error);
      throw new Error('Failed to send email: ' + (error as Error).message);
    }
  }

  // Send document summary email
  static async sendDocumentSummaryEmail(
    emailRequest: EmailRequest,
    summary: string,
    documentTitle?: string,
    pdfUrl?: string
  ): Promise<void> {
    const template = this.generateDocumentEmailTemplate(
      emailRequest.client_name,
      documentTitle || 'Research Document',
      summary,
      emailRequest.language,
      pdfUrl
    );

    await this.sendEmail(emailRequest.client_email, template, {
      clientId: emailRequest.client_id,
      documentId: emailRequest.document_id,
      sentBy: 'system'
    });
  }
} 